// One agent's "about" page: who they are, what they do, how to talk to them. Behind every agent avatar on the web.
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { api } from "@/lib/api";
import { AgentAvatar } from "@/components/agents/Agents";

export default function AgentProfile() {
  const { key } = useParams();
  const [a, setA] = useState(null);
  useEffect(() => { setA(null); api("/agents/" + key + "/profile", { auth: false }).then((d) => setA(d.agent)).catch(() => setA(false)); }, [key]);
  usePageMeta(a ? a.name + " · " + a.role : "Fable agent", a ? a.tagline : "");
  if (a === false) return <div className="mx-auto max-w-3xl px-4 py-16 text-center"><h1 className="text-2xl font-bold">No agent by that name</h1><Link to="/agents" className="mt-3 inline-block text-[#E0007A] underline">Meet your agents</Link></div>;
  if (!a) return <div className="mx-auto max-w-3xl px-4 py-10"><Skeleton className="h-80 rounded-3xl" /></div>;
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6" data-testid={"agent-profile-" + a.key}>
      <section className="overflow-hidden rounded-3xl p-6 sm:p-8" style={{ background: "linear-gradient(160deg, " + a.soft + ", transparent 70%)" }}>
        <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
          <AgentAvatar a={a} size={120} />
          <div className="min-w-0">
            <p className="text-sm font-semibold" style={{ color: a.color }}>{a.role}</p>
            <h1 className="font-serif-display text-4xl font-bold">{a.name}</h1>
            <p className="mt-1 text-muted-foreground">{a.tagline}</p>
            <p className="mt-2 text-xs text-muted-foreground">{a.age} · {a.home} · Fable agent</p>
          </div>
        </div>
        <p className="mt-6 rounded-2xl bg-background/80 p-4 text-[15px] leading-relaxed">{a.about}</p>
      </section>

      <section className="mt-8 grid gap-6 sm:grid-cols-2">
        <div>
          <h2 className="text-lg font-bold">What {a.name} does</h2>
          <ul className="mt-3 space-y-2">
            {(a.does || []).map((t) => <li key={t} className="flex items-start gap-2 text-sm"><Sparkles size={15} className="mt-0.5 shrink-0" style={{ color: a.color }} />{t}</li>)}
          </ul>
        </div>
        <div>
          <h2 className="text-lg font-bold">Try asking</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {(a.asks || []).map((t) => <Link key={t} to={a.where} className="rounded-full border border-border px-3 py-1.5 text-sm hover:bg-muted">“{t}”</Link>)}
          </div>
          {a.style && <p className="mt-4 text-sm text-muted-foreground"><b className="text-foreground">How {a.name} works:</b> {a.style}</p>}
        </div>
      </section>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Link to={a.where}><Button className="rounded-full" style={{ background: a.color }}>Open {a.name} <ArrowRight size={15} className="ml-1" /></Button></Link>
        <Link to="/agents" className="text-sm font-semibold text-muted-foreground hover:underline">All agents</Link>
      </div>
      <p className="mt-10 text-xs text-muted-foreground">{a.name} is an AI agent made by Fable +. Portraits are illustrated characters, not real people.</p>
    </div>
  );
}
